import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import { mapQuestionnairesToFormData } from 'src/components/pages/CoiFormManagement/mapQuestionnaireToFormData';
import {
  FormStatusTab,
  FormType,
} from 'src/components/pages/CoiFormManagement/types';
import { filterCategoriesEligibleForNewForm } from 'src/components/pages/FormCreation/components/CategorySelection/helpers/filterCategoriesEligibleForNewForm';
import {
  getCategoriesListForConfiguration,
  transformSpeakUpCategories,
} from '../components/Configuration/helpers/categoryDataTransformers';
import { isSameCategoryName } from '../components/Configuration/helpers/isSameCategoryName';
import type {
  CategoryType,
  FormManagementQuestionnaireByIdQuery,
  FormManagementQuestionnairesQuery,
  FormManagementQuestionsQuery,
  GetOrganisationCategoriesQuery,
} from 'src/graphql';
import type { LanguageOption } from 'src/utils/languages';
import { DUMMY_DEFAULT_LANGUAGE_ONLY } from '../constants';
import { stripHtml } from '../helpers/validation';
import {
  mapApiQuestionnaireToFormState,
  mapApiQuestionsToFormState,
} from '../mapApiToFormState';
import type { CountryOption, FormCreationMode, QuestionData } from '../types';

type QuestionnaireListItem =
  FormManagementQuestionnairesQuery['formManagementQuestionnaires'][number];

export function speakUpEditDraftCategoryMustBeRechosen(
  categoryName: string,
  currentQuestionnaireId: string,
  status: string | null | undefined,
  listItems: QuestionnaireListItem[] | undefined,
  orgCategories:
    | GetOrganisationCategoriesQuery['getOrganisationCategories']
    | undefined,
): boolean {
  if (!categoryName.trim()) return false;
  if (String(status ?? '').toLowerCase() !== 'draft') return false;

  const forms = listItems?.length ? mapQuestionnairesToFormData(listItems) : [];
  const claimed = forms.some(
    (f) =>
      f.id !== currentQuestionnaireId &&
      f.type === FormType.SPEAK_UP &&
      f.status === FormStatusTab.ACTIVE &&
      isSameCategoryName(f.category, categoryName),
  );
  if (claimed) return true;

  if (orgCategories && orgCategories.length > 0) {
    const eligible = transformSpeakUpCategories(
      filterCategoriesEligibleForNewForm(
        orgCategories as CategoryType[],
        false,
      ),
    );
    if (!eligible.some((c) => isSameCategoryName(c.name, categoryName))) {
      return true;
    }
  }

  return false;
}

export type UseFormCreationEditModeHydrationParams = {
  isSpeakUpForm: boolean;
  isEditMode: boolean;
  isQuestionnaireLoading: boolean;
  isQuestionsLoading: boolean;
  questionnaireData: FormManagementQuestionnaireByIdQuery | undefined;
  questionsData: FormManagementQuestionsQuery | undefined;
  questionnaireIdFromParams: string | undefined;
  setMode: Dispatch<SetStateAction<FormCreationMode>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setDescription: Dispatch<SetStateAction<string>>;
  setTitleTranslations: Dispatch<SetStateAction<Record<string, string>>>;
  setDescriptionTranslations: Dispatch<SetStateAction<Record<string, string>>>;
  setCategoryName: Dispatch<SetStateAction<string>>;
  setCategoryId: Dispatch<SetStateAction<number | null>>;
  setCategoryDescription: Dispatch<SetStateAction<string>>;
  setSelectedLanguages: Dispatch<SetStateAction<LanguageOption[]>>;
  setSelectedCountry: Dispatch<SetStateAction<CountryOption[]>>;
  setQuestionnaireId: Dispatch<SetStateAction<string | null>>;
  setQuestions: Dispatch<SetStateAction<QuestionData[]>>;
  setAutoReviewEnabled: Dispatch<SetStateAction<boolean>>;
  lastSavedStateRef: MutableRefObject<{
    title: string;
    description: string;
    categoryName: string;
    categoryDescription: string;
    questions: QuestionData[];
    selectedLanguages: LanguageOption[];
    selectedCountryCodes: string[];
    autoReviewEnabled: boolean;
  }>;
  questionnairesListData: FormManagementQuestionnairesQuery | undefined;
  isQuestionnairesListLoading: boolean;
  organisationCategoriesData: GetOrganisationCategoriesQuery | undefined;
  isOrganisationCategoriesLoading: boolean;
};

export const useFormCreationEditModeHydration = ({
  isSpeakUpForm,
  isEditMode,
  isQuestionnaireLoading,
  isQuestionsLoading,
  questionnaireData,
  questionsData,
  questionnaireIdFromParams,
  setMode,
  setTitle,
  setDescription,
  setTitleTranslations,
  setDescriptionTranslations,
  setCategoryName,
  setCategoryId,
  setCategoryDescription,
  setSelectedLanguages,
  setSelectedCountry,
  setQuestionnaireId,
  setQuestions,
  setAutoReviewEnabled,
  lastSavedStateRef,
  questionnairesListData,
  isQuestionnairesListLoading,
  organisationCategoriesData,
  isOrganisationCategoriesLoading,
}: UseFormCreationEditModeHydrationParams) => {
  const appliedEditRef = useRef(false);

  useEffect(() => {
    if (!isEditMode) {
      appliedEditRef.current = false;
      return;
    }

    if (appliedEditRef.current) return;

    if (isQuestionnaireLoading || isQuestionsLoading) {
      setMode('loading');
      return;
    }

    if (
      isSpeakUpForm &&
      isEditMode &&
      (isQuestionnairesListLoading || isOrganisationCategoriesLoading)
    ) {
      setMode('loading');
      return;
    }

    const questionnaire = questionnaireData?.formManagementQuestionnaireById;
    if (!questionnaire) return;

    appliedEditRef.current = true;
    const formState = mapApiQuestionnaireToFormState(questionnaire);
    const orgCategories = organisationCategoriesData?.getOrganisationCategories;
    const mustRechooseSpeakUpCategory =
      isSpeakUpForm &&
      speakUpEditDraftCategoryMustBeRechosen(
        formState.categoryName,
        questionnaireIdFromParams!,
        questionnaire.status,
        questionnairesListData?.formManagementQuestionnaires,
        orgCategories,
      );

    let hydratedCategoryDescription = '';
    const normalizedCountryCodes = (formState.countries ?? [])
      .map((country) => country.countryCode.toUpperCase())
      .filter(Boolean)
      .sort();

    if (mustRechooseSpeakUpCategory) {
      setTitle('');
      setDescription('');
      setCategoryName('');
      setCategoryId(null);
      setCategoryDescription('');
      setSelectedLanguages(DUMMY_DEFAULT_LANGUAGE_ONLY);
      setSelectedCountry([]);
      setAutoReviewEnabled(false);
    } else {
      const coiCategories = getCategoriesListForConfiguration(
        transformSpeakUpCategories(
          filterCategoriesEligibleForNewForm(
            orgCategories as CategoryType[],
            !isSpeakUpForm,
          ),
        ),
      );
      const matchedCoiCategory = coiCategories.find((c) =>
        isSameCategoryName(c.name, formState.categoryName),
      );
      hydratedCategoryDescription = matchedCoiCategory?.description ?? '';
      setTitle(formState.title);
      setDescription(formState.description);
      setTitleTranslations(formState.titleTranslations ?? {});
      setDescriptionTranslations(formState.descriptionTranslations ?? {});
      setCategoryName(formState.categoryName);
      setCategoryId(formState.categoryId);
      setCategoryDescription(hydratedCategoryDescription);
      setSelectedLanguages(formState.languages);
      setSelectedCountry(
        (formState.countries ?? []).map((country) => ({
          value: country.countryCode.toUpperCase(),
          label: country.countryName,
        })),
      );
      setAutoReviewEnabled(Boolean(formState.autoReviewEnabled));
    }

    setQuestionnaireId(questionnaireIdFromParams!);

    const apiQuestions =
      questionsData?.formManagementQuestions?.questions ?? [];
    const loadedQuestions =
      apiQuestions.length > 0 ? mapApiQuestionsToFormState(apiQuestions) : [];

    if (loadedQuestions.length > 0) {
      setQuestions(loadedQuestions);
    }

    if (mustRechooseSpeakUpCategory) {
      lastSavedStateRef.current = {
        title: '',
        description: '',
        categoryName: '',
        categoryDescription: '',
        questions: JSON.parse(JSON.stringify(loadedQuestions)),
        selectedLanguages: DUMMY_DEFAULT_LANGUAGE_ONLY,
        selectedCountryCodes: [],
        // VAULT-19456: Auto review is locked to enabled by default
        autoReviewEnabled: !isSpeakUpForm,
      };
    } else {
      lastSavedStateRef.current = {
        title: formState.title,
        description: stripHtml(formState.description).trim(),
        categoryName: formState.categoryName,
        categoryDescription: stripHtml(hydratedCategoryDescription).trim(),
        questions: JSON.parse(JSON.stringify(loadedQuestions)),
        selectedLanguages: formState.languages,
        selectedCountryCodes: normalizedCountryCodes,
        autoReviewEnabled: Boolean(formState.autoReviewEnabled),
      };
    }

    setMode('edit');
  }, [
    isSpeakUpForm,
    isEditMode,
    isQuestionnaireLoading,
    isQuestionsLoading,
    isQuestionnairesListLoading,
    isOrganisationCategoriesLoading,
    questionnaireData,
    questionsData,
    questionnaireIdFromParams,
    questionnairesListData,
    organisationCategoriesData,
    setMode,
    setTitle,
    setDescription,
    setCategoryName,
    setCategoryId,
    setCategoryDescription,
    setSelectedLanguages,
    setSelectedCountry,
    setQuestionnaireId,
    setQuestions,
    setAutoReviewEnabled,
    lastSavedStateRef,
    setTitleTranslations,
    setDescriptionTranslations,
  ]);
};
